import clsx from 'clsx'
import dayjs from 'dayjs'
import { useAtomValue, useSetAtom } from 'jotai'
import { delay, filter, merge } from 'lodash'
import { useMemo, useState } from 'react'
import useOffers from 'src/common/apollo/hooks/useOffers'
import {
  OfferOrderBy,
  OffersQueryVariables,
  OrderDirection,
} from 'src/common/apollo/types/swarmx/graphql'
import useBreakpoint from 'src/common/hooks/ui/useBreakpoint'
import InfiniteListProvider from 'src/common/ui-kit/InfiniteList/InviteListContext'
import { unifyAddress } from 'src/common/utils/web3/unifyAddress'
import { editOfferAtom } from 'src/pages/DiscoverPage/discover-store'
import EditOffer from './components/EditOffer/EditOffer'
import OffersFilter from './components/OffersFilter/OffersFilter'
import OffersList from './components/OffersList/OffersList'
import OffersNoAccount from './components/OffersList/OffersNoAccount'
import OfferTabs from './components/OfferTabs'
import { OffersManagementTab } from './components/types'
import useSmartAccount from 'src/common/api-clients/safe-protocol-kit/useSmartAccount'
import { NetworkId, whitelabeledTokens } from 'src/common/consts'
import { lastChainIdAtom } from 'src/common/stores/store'

interface OffersManagementProps {
  tab: OffersManagementTab
}

const OffersManagement: React.FC<OffersManagementProps> = ({ tab = 'all' }) => {
  const { smartAccountInfo } = useSmartAccount()
  const [offersFilter, setOffersFilter] =
    useState<OffersQueryVariables['filter']>()
  const setEditOffer = useSetAtom(editOfferAtom)
  const chainId = useAtomValue(lastChainIdAtom) as NetworkId
  const whitelabeledTokensForNetwork = whitelabeledTokens[chainId]

  const is2xl = useBreakpoint('2xl')
  const isMd = useBreakpoint('md')

  const filterByTab = useMemo(() => {
    switch (tab) {
      case 'all':
        return {
          isPrivate: false,
          expiryTimestamp_gt: dayjs().unix().toString(),
        }
      case 'my':
        return { maker_contains_nocase: smartAccountInfo?.address }
      case 'private':
        return {
          ...(smartAccountInfo?.address && {
            specialAddresses_contains_nocase: [
              unifyAddress(smartAccountInfo?.address),
            ],
          }),
          expiryTimestamp_gt: dayjs().unix().toString(),
        }
    }
  }, [smartAccountInfo?.address, tab])

  const mergedFilter = useMemo(() => {
    return {
      ...offersFilter,
      ...filterByTab,
      depositAsset_: merge(
        {
          id_in: whitelabeledTokensForNetwork.map(a => a.toLowerCase()),
        },
        offersFilter?.depositAsset_
      ),
      withdrawalAsset_: merge(
        {
          id_in: whitelabeledTokensForNetwork.map(a => a.toLowerCase()),
        },
        offersFilter?.withdrawalAsset_
      ),
    }
  }, [filterByTab, offersFilter, whitelabeledTokensForNetwork])

  const { offers, fetchMore, hasMore, loading, refetch } = useOffers({
    variables: {
      filter: mergedFilter,
      orderBy: OfferOrderBy.CreatedAt,
      orderDirection: OrderDirection.Desc,
    },
    skip: !filter,
  })

  const handleSuccessfulEdit = () => {
    setEditOffer({ offer: null, open: false })
    delay(() => refetch(), 1000)
  }

  return (
    <div
      className={clsx(
        `grid gap-4`,
        !is2xl
          ? 'grid-cols-[minmax(0,1fr)_768px_minmax(0,1fr)]'
          : 'grid-cols-[minmax(0,1fr)]'
      )}
    >
      <div className='flex flex-col gap-2'></div>

      <div
        className={clsx(
          'flex flex-col gap-2',
          is2xl && !isMd && 'max-w-3xl mx-auto'
        )}
      >
        <div>
          <OfferTabs tab={tab} />
        </div>
        <OffersFilter onFilterChange={setOffersFilter} />
        {!smartAccountInfo && tab !== 'all' ? (
          <OffersNoAccount />
        ) : (
          <InfiniteListProvider
            hasMore={hasMore}
            loading={loading}
            loadMore={fetchMore}
          >
            <OffersList offers={offers} />
          </InfiniteListProvider>
        )}
      </div>

      <div className='relative'>
        <div className='fixed pr-6'>
          <EditOffer onSuccessfulEdit={handleSuccessfulEdit} />
        </div>
      </div>
    </div>
  )
}

export default OffersManagement
